import { useState, useEffect, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { airlines } from '../lib/airlines'; // Importa a nova lista de companhias

// Hook customizado para "debouncing"
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

export default function AirlineAutocomplete({ value, onSelect, placeholder = "Selecione..." }) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const debouncedSearchQuery = useDebounce(inputValue, 200);

    const selectedAirline = useMemo(() => 
        airlines.find(airline => airline.iata.toLowerCase() === value?.toLowerCase()),
    [value]);

    const filteredAirlines = useMemo(() => {
        if (!debouncedSearchQuery) {
            // Retorna uma lista menor se não houver busca, para melhor performance inicial
            return airlines.slice(0, 50); 
        }
        const lowercasedQuery = debouncedSearchQuery.toLowerCase();
        return airlines.filter(airline =>
            airline.iata.toLowerCase().includes(lowercasedQuery) ||
            airline.name.toLowerCase().includes(lowercasedQuery)
        ).slice(0, 50); // Limita os resultados da busca
    }, [debouncedSearchQuery]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between font-normal"
                >
                    {selectedAirline
                        ? `${selectedAirline.name} (${selectedAirline.iata})`
                        : placeholder}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0">
                <div className="p-2 border-b">
                    <Input
                        placeholder="Buscar por nome ou IATA..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className="w-full"
                        autoFocus
                    />
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                    {filteredAirlines.length > 0 ? (
                        filteredAirlines.map((airline) => (
                            <button
                                key={airline.iata}
                                onClick={() => {
                                    onSelect(airline); // Envia o objeto completo (iata, name)
                                    setOpen(false);
                                    setInputValue(""); // Limpa input após seleção
                                }}
                                className="w-full text-left p-2 hover:bg-gray-100 flex items-center justify-between rounded-sm text-sm" // Ajuste de tamanho
                            >
                                <span className="font-medium">{airline.name} ({airline.iata})</span>
                                <Check
                                    className={cn(
                                        "h-4 w-4",
                                        value?.toLowerCase() === airline.iata.toLowerCase() ? "opacity-100" : "opacity-0"
                                    )}
                                />
                            </button>
                        ))
                    ) : (
                        <p className="p-4 text-sm text-center text-gray-500">Nenhuma companhia encontrada.</p>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

